import {Link, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Button} from '../../ui/buttons/button';
import {Form} from '../../ui/forms/form';
import {LoginPayload, useLogin} from '../requests/use-login';
import {FormCheckbox} from '../../ui/forms/toggle/checkbox';
import {LinkStyle} from '../../ui/buttons/external-link';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useContext, useState} from 'react';
import {
  SiteConfigContext,
  SiteConfigContextValue,
} from '../../core/settings/site-config-context';
import {useSettings} from '../../core/settings/use-settings';

interface Props {
  onTwoFactorChallenge: () => void;
}
export function LoginPage({onTwoFactorChallenge}: Props) {
  const [searchParams] = useSearchParams();
  const {pathname} = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const isWorkspaceLogin = pathname.includes('workspace');
  const searchParamsEmail = searchParams.get('email') || undefined;

  const {branding, registration, site, social} = useSettings();
  const siteConfig = useContext(SiteConfigContext);

  const demoDefaults =
    site.demo && !searchParamsEmail ? getDemoFormDefaults(siteConfig) : {};
  const form = useForm<LoginPayload>({
    defaultValues: {remember: true, email: searchParamsEmail, ...demoDefaults},
  });
  const login = useLogin(form);

  const heading = isWorkspaceLogin ? (
    <Trans
      values={{siteName: branding?.site_name}}
      message="To join your team on :siteName, login to your account"
    />
  ) : (
    <Trans message="Sign in to your account" />
  );

  const message = "";

  const isInvalid = !!Object.keys(form.formState.errors).length;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const PasswordIcon = () => {
    return showPassword ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <g clipPath="url(#clip0_160_536)">
          <path d="M17.4532 7.06421C16.2899 5.16971 13.6439 1.99121 8.99992 1.99121C4.35592 1.99121 1.70992 5.16971 0.546666 7.06421C0.187062 7.64584 -0.00341797 8.31614 -0.00341797 8.99996C-0.00341797 9.68378 0.187062 10.3541 0.546666 10.9357C1.70992 12.8302 4.35592 16.0087 8.99992 16.0087C13.6439 16.0087 16.2899 12.8302 17.4532 10.9357C17.8128 10.3541 18.0033 9.68378 18.0033 8.99996C18.0033 8.31614 17.8128 7.64584 17.4532 7.06421ZM16.1744 10.1505C15.1754 11.775 12.9142 14.5087 8.99992 14.5087C5.08567 14.5087 2.82442 11.775 1.82542 10.1505C1.61177 9.80475 1.4986 9.40637 1.4986 8.99996C1.4986 8.59356 1.61177 8.19518 1.82542 7.84946C2.82442 6.22496 5.08567 3.49121 8.99992 3.49121C12.9142 3.49121 15.1754 6.22196 16.1744 7.84946C16.3881 8.19518 16.5012 8.59356 16.5012 8.99996C16.5012 9.40637 16.3881 9.80475 16.1744 10.1505Z" fill="#4D4D4D"/>
          <path d="M8.99985 5.24994C8.25817 5.24994 7.53315 5.46988 6.91646 5.88193C6.29978 6.29399 5.81913 6.87966 5.5353 7.56488C5.25148 8.25011 5.17721 9.00411 5.32191 9.73153C5.4666 10.459 5.82376 11.1271 6.3482 11.6516C6.87265 12.176 7.54083 12.5332 8.26826 12.6779C8.99569 12.8226 9.74969 12.7483 10.4349 12.4645C11.1201 12.1807 11.7058 11.7 12.1179 11.0833C12.5299 10.4666 12.7499 9.74163 12.7499 8.99995C12.7487 8.00575 12.3532 7.05261 11.6502 6.34961C10.9472 5.64661 9.99405 5.25114 8.99985 5.24994ZM8.99985 11.2499C8.55484 11.2499 8.11983 11.118 7.74982 10.8708C7.37981 10.6235 7.09142 10.2721 6.92112 9.86098C6.75083 9.44985 6.70627 8.99745 6.79308 8.56099C6.8799 8.12454 7.09419 7.72362 7.40886 7.40895C7.72353 7.09429 8.12444 6.87999 8.5609 6.79318C8.99736 6.75092 9.44976 6.92122 9.86089 6.92122C10.272 7.09151 10.6234 7.3799 10.8707 7.74991C11.1179 8.11992 11.2499 8.55494 11.2499 8.99995C11.2499 9.59668 11.0128 10.169 10.5908 10.5909C10.1689 11.0129 9.59659 11.2499 8.99985 11.2499Z" fill="#4D4D4D"/>
        </g>
        <defs>
          <clipPath id="clip0_160_536">
            <rect width="18" height="18" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <g clipPath="url(#clip0_160_264)">
          <path d="M17.4532 7.06401C16.7761 5.95531 15.922 4.96496 14.9249 4.13226L17.0249 2.03226C17.1615 1.89081 17.2371 1.70136 17.2354 1.50471C17.2337 1.30807 17.1548 1.11996 17.0158 0.9809C16.8767 0.841844 16.6886 0.762968 16.492 0.761259C16.2953 0.75955 16.1059 0.835146 15.9644 0.971764L13.6807 3.25851C12.265 2.41763 10.6465 1.97936 8.99992 1.99101C4.35667 1.99101 1.71067 5.16951 0.546666 7.06401C0.187062 7.64564 -0.00341797 8.31594 -0.00341797 8.99976C-0.00341797 9.68358 0.187062 10.3539 0.546666 10.9355C1.22373 12.0442 2.07778 13.0346 3.07492 13.8673L0.974916 15.9673C0.903284 16.0364 0.846147 16.1192 0.80684 16.2107C0.767534 16.3022 0.746844 16.4006 0.745978 16.5002C0.745113 16.5998 0.764089 16.6986 0.8018 16.7907C0.839511 16.8829 0.895201 16.9666 0.96562 17.0371C1.03604 17.1075 1.11978 17.1632 1.21195 17.2009C1.30412 17.2386 1.40288 17.2576 1.50247 17.2567C1.60205 17.2558 1.70047 17.2351 1.79197 17.1958C1.88347 17.1565 1.96623 17.0994 2.03542 17.0278L4.32442 14.7388C5.73837 15.5795 7.35492 16.0185 8.99992 16.0085C13.6432 16.0085 16.2892 12.83 17.4532 10.9355C17.8128 10.3539 18.0033 9.68358 18.0033 8.99976C18.0033 8.31594 17.8128 7.64564 17.4532 7.06401ZM1.82467 10.1503C1.61102 9.80455 1.49785 9.40617 1.49785 8.99976C1.49785 8.59336 1.61102 8.19498 1.82467 7.84926C2.82517 6.22476 5.08642 3.49101 8.99992 3.49101C10.2451 3.48404 11.4728 3.78414 12.5744 4.36476L11.0647 5.87451C10.3446 5.39647 9.48135 5.18226 8.62134 5.26825C7.76133 5.35424 6.95757 5.73512 6.34642 6.34627C5.73527 6.95741 5.35439 7.76118 5.2684 8.62119C5.18241 9.48119 5.39662 10.3445 5.87467 11.0645L4.14217 12.797C3.22349 12.0543 2.43954 11.159 1.82467 10.1503ZM11.2499 8.99976C11.2499 9.5965 11.0129 10.1688 10.5909 10.5908C10.1689 11.0127 9.59665 11.2498 8.99992 11.2498C8.6658 11.2485 8.33631 11.1715 8.03617 11.0248L11.0249 8.03601C11.1717 8.33616 11.2486 8.66565 11.2499 8.99976ZM6.74992 8.99976C6.74992 8.40303 6.98697 7.83073 7.40893 7.40877C7.83088 6.98682 8.40318 6.74976 8.99992 6.74976C9.33403 6.75106 9.66352 6.82798 9.96367 6.97476L6.97492 9.96351C6.82813 9.66336 6.75121 9.33388 6.74992 8.99976ZM16.1752 10.1503C15.1747 11.7748 12.9134 14.5085 8.99992 14.5085C7.75471 14.5155 6.52699 14.2154 5.42542 13.6348L6.93517 12.125C7.65522 12.6031 8.51849 12.8173 9.37849 12.7313C10.2385 12.6453 11.0423 12.2644 11.6534 11.6533C12.2646 11.0421 12.6454 10.2383 12.7314 9.37834C12.8174 8.51833 12.6032 7.65506 12.1252 6.93501L13.8577 5.20251C14.7763 5.94522 15.5603 6.84055 16.1752 7.84926C16.3888 8.19498 16.502 8.59336 16.502 8.99976C16.502 9.40617 16.3888 9.80455 16.1752 10.1503Z" fill="#4D4D4D"/>
        </g>
        <defs>
          <clipPath id="clip0_160_264">
            <rect width="18" height="18" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    );
  };

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Login" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={payload => {
          login.mutate(payload, {
            onSuccess: response => {
              if (response.two_factor) {
                onTwoFactorChallenge();
              }
            },
          });
        }}
      >
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          label={<Trans message="Email" />}
          disabled={!!searchParamsEmail}
          invalid={isInvalid}
          required
        />
        <div className="relative">
          <FormTextField
            className="mb-12"
            name="password"
            type={showPassword ? "text" : "password"}
            label={<Trans message="Password" />}
            invalid={isInvalid}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
             style={{ marginTop: "20px" }}
          >
            <PasswordIcon />
          </button>
        </div>
        <div id='forgot_password'>
          <Link className={LinkStyle} to="/forgot-password" tabIndex={-1} id='forgot_password_label'>
            <Trans message="Şifreni mi unuttun?" />
          </Link>
        </div>
        
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={login.isPending}
          id='login_button'
        >
          <Trans message="Giriş yap" />
        </Button>
      </Form>
      <SocialAuthSection
        dividerMessage={
          social.compact_buttons ? (
            <Trans message="Or sign in with" />
          ) : (
            <Trans message="OR" />
          )
        }
      />
      <div id='register_label'><label> <Trans message="Hesabın yok mu?" />  <a href="/register" id="register_button"><Trans message="Hemen kayıt ol" /> </a></label></div>
    </AuthLayout>
  );
}

function getDemoFormDefaults(siteConfig: SiteConfigContextValue) {
  if (siteConfig.demo.loginPageDefaults === 'randomAccount') {
    // random number between 0 and 100, padded to 3 digits
    const number = Math.floor(Math.random() * 100) + 1;
    const paddedNumber = String(number).padStart(3, '0');
    return {
      email: `admin@demo${paddedNumber}.com`,
      password: 'admin',
    };
  } else {
    return {
      email: siteConfig.demo.email ?? 'admin@admin.com',
      password: siteConfig.demo.password ?? 'admin',
    };
  }
}